import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { LocalStorageService } from 'angular-web-storage';

import * as services from '../../../services';
import * as viewmodels from '../../../viewmodels';
import * as models from '../../../models';

//dialogs
import { MatDialog } from '@angular/material/dialog';
import {
  HarvestAddComponent, HarvestGroupDeleteComponent, RestrictionNotificationComponent
} from '../../../dialogs';
import { Subscription } from 'rxjs';
import { AppStorage } from '../../../services';
// import { TimelineComponent } from 'app/components/timeline/timeline.component';
import { TimelineHelper } from 'app/components/timeline/timeline-helper';
import { TemplateSelectMultiBtnComponent } from 'app/buttons/template-select-multi-btn/template-select-multi-btn.component';
import { LeaflogixSyncBtnComponent } from 'app/buttons/leaflogix-sync-btn/leaflogix-sync-btn.component';
import { Timestamp, getDocs } from '@angular/fire/firestore';
import { HarvestSelectMultiBtnComponent } from 'app/buttons/harvest-select-multi-btn/harvest-select-multi-btn.component';
import { ZoneSelectMultiBtnComponent } from 'app/buttons/zone-select-multi-btn/zone-select-multi-btn.component';

@Component({
    selector: 'app-harvest-list',
    templateUrl: './harvest-list.component.html',
    styleUrls: ['./harvest-list.component.scss'],
    standalone: false
})
export class HarvestListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(TemplateSelectMultiBtnComponent) templateSelectMultiBtnComponent: TemplateSelectMultiBtnComponent;
  @ViewChild(HarvestSelectMultiBtnComponent) harvestSelectMultiBtnComponent: HarvestSelectMultiBtnComponent;
  @ViewChild(ZoneSelectMultiBtnComponent) zoneSelectMultiBtnComponent: ZoneSelectMultiBtnComponent;
  @ViewChild(LeaflogixSyncBtnComponent) leaflogixSyncBtnComponent: LeaflogixSyncBtnComponent;

  companyId: string;
  company: models.Company;
  companySub: Subscription;
  harvestsSub: Subscription;

  templateIds: Array<string> = [];
  allTemplatesSelected: boolean = false;

  postHarvestDataPoints: models.PostHarvest[];

  timelineData: Array<any>;
  timelineDateFormat: string = "MMM";
  timelineColors: Array<string>;
  harvests: Array<viewmodels.HarvestTableViewModel> = [];
  maxPhases: number = 0;
  phases: Array<viewmodels.HarvestTableViewPhaseModel> = [];
  harvestIds: string[] = [];
  zoneIds: string[] = [];
  filteredHarvestPhasesByZone: Array<viewmodels.HarvestPhases> = [];
  harvestListSortDirection: 'asc' | 'desc' = 'desc';

  get numberOfHarvests(): number {
    return this.tableRows.length;
  }

  get canAdd(): boolean {
    if (this.helperService.company_freeVersion && this.numberOfHarvests >= this.appStorage.freeHarvests) {
      return false;
    }

    return true;
  }

  constructor(
    private claimsService: services.ClaimsService
    , private harvestService: services.HarvestService
    , private eventService: services.EventService
    , private helperService: services.HelperService
    , private router: Router
    , private route: ActivatedRoute
    , private storage: LocalStorageService
    , private dialog: MatDialog
    , public appStorage: AppStorage
    , private strainService: services.StrainService
    , private postHarvestService: services.PostHarvestService
    , private timelineHelper: TimelineHelper
    , private zoneService: services.ZoneService
  ) {
    this.harvestListSortDirection = this.appStorage.harvestListSortDesc ? 'desc' : 'asc';
  }

  /** Persist the user's Clone Date sort direction (was ag-grid's onSortChanged). */
  onTableSort(event: { field: string, order: number }) {
    if (event.field === 'startDateMs') {
      this.appStorage.harvestListSortDesc = event.order === -1;
    }
  }

  async ngOnInit() {
  }

  // --- PrimeNG spike state & helpers -----------------------------------------
  /** Flat rows for the p-table: harvest VM + numeric date keys for sorting. */
  tableRows: Array<any> = [];
  tableLoading: boolean = true;
  /** Ordered phase names (used for timeline colors + CSV export). */
  phaseColumns: Array<string> = [];
  /** Only show the Dutchie column when at least one harvest has a name. */
  showDutchieColumn: boolean = false;

  /** Green progression used to colour phase segments by stage order. */
  private static readonly PHASE_COLORS = ['#C0DD97', '#97C459', '#639922', '#3B6D11', '#27500A', '#173404'];

  /** Build the p-table rows (+ inline phase timeline) from the loaded harvests. */
  buildTableRows() {
    this.showDutchieColumn = (this.harvests || []).some(
      h => h.leaflogixName != null && h.leaflogixName !== ''
    );
    this.phaseColumns = (this.phases || []).map(p => p.name);
    this.tableRows = (this.harvests || []).map(h => {
      const row: any = {
        ...(h as any),
        startDateMs: this.tsToMs(h.startDate),
        harvestDateMs: this.tsToMs(h.harvestDate),
        endDateMs: this.tsToMs(h.endDate),
      };
      row.timeline = this.buildTimeline(row);
      return row;
    });
  }

  /**
   * Build inline phase segments for a harvest. Segment width is the duration
   * to the next phase's start (last segment runs to the harvest/end date), so
   * we only need each phase's start date — which is already on the row.
   */
  private buildTimeline(row: any): Array<any> {
    const segs = this.phaseColumns
      .map(name => ({ name, start: row[name] }))
      .filter(s => s.start && s.start.seconds != null)
      .sort((a, b) => a.start.seconds - b.start.seconds);
    if (segs.length === 0) { return []; }

    const endMs = row.harvestDateMs > 0 ? row.harvestDateMs
                : (row.endDateMs > 0 ? row.endDateMs : null);
    const colors = HarvestListComponent.PHASE_COLORS;
    const DAY = 86400000;

    return segs.map((s, i) => {
      const startMs = s.start.seconds * 1000;
      const nextMs = i < segs.length - 1
        ? segs[i + 1].start.seconds * 1000
        : (endMs ?? startMs + 7 * DAY);
      const weight = Math.max(1, Math.round((nextMs - startMs) / DAY));
      const idx = this.phaseColumns.indexOf(s.name);
      const color = colors[Math.min(idx < 0 ? i : idx, colors.length - 1)];
      return { name: s.name, start: s.start, weight, color };
    });
  }

  private tsToMs(ts: any): number {
    return ts?.seconds != null ? ts.seconds * 1000 : -1;
  }

  formatDate(ts: any): string {
    if (ts == null) { return ''; }
    return this.helperService.timestampToFormattedDateString(ts);
  }

  openHarvest(h: any) {
    if (h?.harvestId) {
      this.router.navigate(['console', 'harvest', h.harvestId]);
    }
  }

  /** Columns rendered before the Plant Count column (for footer alignment). */
  get colsBeforePlantCount(): number {
    // Name, Clone, Phase timeline, Harvest, End, Completed, Strains = 7
    return 7 + (this.showDutchieColumn ? 1 : 0);
  }

  get totalColumnCount(): number {
    return this.colsBeforePlantCount + 2; // + Plant Count + Zone
  }

  get totalPlantCount(): number {
    return (this.tableRows || []).reduce((sum, r) => sum + (Number(r.plantCount) || 0), 0);
  }

  ngAfterViewInit(): void {
    //get companyId
    this.companyId = this.helperService.currentCompanyId;
    //if we have it then load
    if (this.companyId != null) {
      //this.getStrains();
      //this.getHarvests();
      setTimeout(() => {
        this.templateSelectMultiBtnComponent.getAll();
      }, 50)
    }
    //set up subscription to listen to company id changes
    this.company = this.helperService.currentCompany;
    this.companySub = this.helperService.getCurrentCompany().subscribe(data => {
      this.company = this.helperService.currentCompany;
      this.templateSelectMultiBtnComponent.firstLoad = true;
      this.templateSelectMultiBtnComponent.getAll();
    })
  }

  ngOnDestroy() {
    if (this.harvestsReloadTimer != null) {
      clearTimeout(this.harvestsReloadTimer);
    }
    if (this.companySub != null) {
      this.companySub.unsubscribe();
    }
    if (this.harvestsSub != null) {
      this.harvestsSub.unsubscribe();
    }
  }

  setUpTutorial() {
    //this.storage.remove('completed-harvest-list-demo');
    let completedDemo = this.storage.get('completed-harvest-list-demo');

    if (completedDemo === true) {
      return;
    }
  }

  // async getStrains() {
  //   let strains = await this.strainService.getAll();
  //   strains.forEach(strain => {
  //     let col: ColDef = {
  //       headerName: strain.name + ' Clones',
  //       field: strain.id + '_clone'
  //     }
  //     this.allPossibleColumns.push(col);
  //   });
  //   this.setUpColumns();
  // }

  // async toggleShowAllHarvests(event) {
  //   await this.getHarvests(event.checked)
  //   this.gridOptions.api.refreshCells();
  // }

  zones: models.Zone[];
  async getZones() {
    let dbZone = await this.zoneService.getDbZone();
    if(dbZone == null){
      return;
    }
    this.zones = dbZone.list;
  }

  /**
   * Coalesce the burst of calls fired during init — the filter buttons each
   * emit (loaded)/(onClosed) (the harvest button emits `loaded` twice per
   * load) and the company subscription kicks too — into a single Firestore
   * load. Without this we opened several snapshotChanges() listeners back to
   * back and rebuilt the table/timeline each time, hence the multiple paints.
   */
  private harvestsReloadTimer: any;
  getHarvests() {
    if (this.harvestsReloadTimer != null) {
      clearTimeout(this.harvestsReloadTimer);
    }
    this.tableLoading = true;
    this.harvestsReloadTimer = setTimeout(() => this.loadHarvests(), 150);
  }

  async loadHarvests() {
    if (this.harvestsSub != null) {
      this.harvestsSub.unsubscribe();
    }
    await this.getZones();

    if (this.zoneIds != null && this.zoneIds.length > 0) {
      this.filteredHarvestPhasesByZone = await this.harvestService.getHarvestsAndPhasesByZoneIds(this.zoneIds);
    }

    this.harvestsSub = (await this.harvestService.getAll(false, !this.appStorage.showFinishedHarvests)).snapshotChanges().subscribe(async snapshot => {
      this.phases = [];
      
      //let harvestDataList = await (await this.harvestService.getAll()).get().toPromise();
      let listModels: Array<viewmodels.HarvestTableViewModel> = [];
      for await (const content of snapshot.map(async harvestData => {
        let harvest: models.Harvest = harvestData.payload.doc.data();
        // if (!includeInactive && harvest.endDate < Timestamp.now()) {
        //   return
        // }
        let harvestModel: viewmodels.HarvestTableViewModel = {
          harvest: harvest,
          harvestId: harvestData.payload.doc.id,
          name: harvest.name,
          completed: harvest.completed,
          templateId: harvest.templateId,
          // startDate: Timestamp.fromDate(this.helperService.addYearsToToday(5)),
          // endDate: Timestamp.fromDate(this.helperService.addYearsToToday(-5))
          startDate: harvest.startDate,
          harvestDate: harvest.harvestDate ?? harvest.endDate,
          endDate: harvest.endDate,
          completedTasks: harvest.completedTasks,
          totalTasks: harvest.totalTasks,
          percentComplete: harvest.completed ? 100 : (harvest.completedTasks / harvest.totalTasks) * 100,
          color: harvest.color,
          leaflogixName: harvest.leaflogixName,
          strains: this.getStrainsFromHarvest(harvest),
          plantCount: this.getPlantCountFromHarvest(harvest),
          zone: this.getZoneForHarvest(harvest)
        }

        if(this.company.eventsSplit) {//if we are configured to split events
          harvest.phases?.forEach(phase => {
            harvestModel[phase.phaseName] = phase.start;
  
            if(this.phases.find(i => i.name == phase.phaseName) == null){
              let phaseVM: viewmodels.HarvestTableViewPhaseModel = {
                name: phase.phaseName,
                startDate: phase.start,
                endDate: phase.end
              }
              this.phases.push(phaseVM);
            }
          });

        } else {
          let eventDataList = await getDocs(this.eventService.get(harvestData.payload.doc.id));
  
          let events: models.Event[]
          eventDataList.forEach(eventData => {
              let dbEvent: models.DbEvent = eventData.data()
              events = dbEvent.list
          })
  
          let phases = this.buildPhaseDataArray(events);
          phases.forEach(phase => {
            harvestModel[phase.name] = phase.startDate;
  
            if(this.phases.find(i => i.name == phase.name) == null){
              this.phases.push(phase);
            }
          });
        }

        // check and include harvest if current harvestModel is in the harvestIds selected AND in selected templateIds
        // excluding harvest either if not in selected harvestIds or in selected templateIds
        if (this.harvestIds.includes(harvestModel.harvestId) && (this.allTemplatesSelected || this.templateIds.includes(harvestModel.templateId))) {
          listModels.push(harvestModel);
        }
        // listModels.push(harvestModel);
        // //set clone counts
        // if(harvest.phases != null && harvest.phases[0] != null && harvest.phases[0].strains != null){
        //   harvest.phases[0].strains.forEach(strain => {
        //     harvestModel[strain.strainId + '_clone'] = strain.count;
        //   });

        //   harvestModel['total_clone'] = this.helperService.sum(harvest.phases[0].strains.map(i => i.count));
        // }

        // //get post harvest data
        // if(harvest.postHarvestData != null){
        //   let keys = Object.keys(harvest.postHarvestData);
        //   keys.forEach(key => {
        //     harvestModel[key + '_post'] = harvest.postHarvestData[key];
        //   });
        // }

        //set start and end date
        // if (harvestModel.startDate == null || harvestModel.endDate == null) {
        //   // let eventDataList = await this.eventService.get(harvestData.payload.doc.id).get().toPromise();
        //   console.log('going through herherhere')
          
        //   harvestModel.startDate = Timestamp.fromDate(this.helperService.addYearsToToday(5));
        //   harvestModel.endDate = Timestamp.fromDate(this.helperService.addYearsToToday(-5));
        //   console.log(events);
        //   events.forEach(event => {
        //     if (event.startDateTime < harvestModel.startDate) {
        //       harvestModel.startDate = event.startDateTime
        //     }
        //     if (event.startDateTime > harvestModel.endDate) {
        //       harvestModel.endDate = event.startDateTime;
        //     }
        //   })
          
          
          
        //   eventDataList.forEach(eventData => {
        //     let dbEvent: models.DbEvent = eventData.data();

        //     harvestModel.startDate = Timestamp.fromDate(this.helperService.addYearsToToday(5));
        //     harvestModel.endDate = Timestamp.fromDate(this.helperService.addYearsToToday(-5));

        //     dbEvent.list.forEach(event => {
        //       if (event.startDateTime < harvestModel.startDate) {
        //         harvestModel.startDate = event.startDateTime
        //       }
        //       if (event.startDateTime > harvestModel.endDate) {
        //         harvestModel.endDate = event.startDateTime;
        //       }
        //     });
        //   });
        // }

        //put all users returned from the server into the list
      }));

      // check if harvests in listModels are from the zones selected
      if (this.filteredHarvestPhasesByZone.length > 0) {
        let harvestsInZone = this.filteredHarvestPhasesByZone.map(_harvest => _harvest.harvestId);
        listModels = listModels.filter(_harvest => harvestsInZone.includes(_harvest.harvestId));
      }

      this.harvests = listModels;
      this.buildTableRows();
      this.tableLoading = false;
      let data = this.timelineHelper.createTimelineDataFromHarvestViewModelList(listModels, false)
      this.timelineData = data.dataList
      this.timelineColors = data.colors
    })
      //this.setTotalRow();

      // snapshot.forEach(async harvestData => {
      //   let harvest: models.Harvest = harvestData.payload.doc.data();
      //   let harvestModel: viewmodels.HarvestTableViewModel = {
      //     harvestId: harvestData.payload.doc.id,
      //     name: harvest.name,
      //     completed: harvest.completed,
      //     templateId: harvest.templateId,
      //     // startDate: Timestamp.fromDate(this.helperService.addYearsToToday(5)),
      //     // endDate: Timestamp.fromDate(this.helperService.addYearsToToday(-5))
      //     startDate: harvest.startDate,
      //     harvestDate: harvest.harvestDate ?? harvest.endDate,
      //     endDate: harvest.endDate,
      //     completedTasks: harvest.completedTasks,
      //     totalTasks: harvest.totalTasks,
      //     percentComplete: harvest.completed ? 100 : (harvest.completedTasks / harvest.totalTasks) * 100,
      //     color: harvest.color,
      //   }

      //   let eventDataList = await this.eventService.get(harvestData.payload.doc.id).get().toPromise();
      //   console.log('harvest id', harvestData.payload.doc.id);
      //   let events: models.Event[]
      //   eventDataList.forEach(eventData => {
      //       let dbEvent: models.DbEvent = eventData.data()
      //       events = dbEvent.list
      //       // console.log(events);
      //   })

      //   harvestModel.phases = this.buildPhaseDataArray(events);

      //   // //set clone counts
      //   // if(harvest.phases != null && harvest.phases[0] != null && harvest.phases[0].strains != null){
      //   //   harvest.phases[0].strains.forEach(strain => {
      //   //     harvestModel[strain.strainId + '_clone'] = strain.count;
      //   //   });

      //   //   harvestModel['total_clone'] = this.helperService.sum(harvest.phases[0].strains.map(i => i.count));
      //   // }

      //   // //get post harvest data
      //   // if(harvest.postHarvestData != null){
      //   //   let keys = Object.keys(harvest.postHarvestData);
      //   //   keys.forEach(key => {
      //   //     harvestModel[key + '_post'] = harvest.postHarvestData[key];
      //   //   });
      //   // }

      //   //set start and end date
      //   if (harvestModel.startDate == null || harvestModel.endDate == null) {
      //     // let eventDataList = await this.eventService.get(harvestData.payload.doc.id).get().toPromise();
      //     console.log('going through herherhere')
          
      //     // harvestModel.startDate = Timestamp.fromDate(this.helperService.addYearsToToday(5));
      //     // harvestModel.endDate = Timestamp.fromDate(this.helperService.addYearsToToday(-5));
      //     // console.log(events);
      //     // events.forEach(event => {
      //     //   if (event.startDateTime < harvestModel.startDate) {
      //     //     harvestModel.startDate = event.startDateTime
      //     //   }
      //     //   if (event.startDateTime > harvestModel.endDate) {
      //     //     harvestModel.endDate = event.startDateTime;
      //     //   }
      //     // })
          
          
          
      //     // eventDataList.forEach(eventData => {
      //     //   let dbEvent: models.DbEvent = eventData.data();

      //     //   harvestModel.startDate = Timestamp.fromDate(this.helperService.addYearsToToday(5));
      //     //   harvestModel.endDate = Timestamp.fromDate(this.helperService.addYearsToToday(-5));

      //     //   dbEvent.list.forEach(event => {
      //     //     if (event.startDateTime < harvestModel.startDate) {
      //     //       harvestModel.startDate = event.startDateTime
      //     //     }
      //     //     if (event.startDateTime > harvestModel.endDate) {
      //     //       harvestModel.endDate = event.startDateTime;
      //     //     }
      //     //   });
      //     // });
      //   }

      //   //put all users returned from the server into the list
      //   listModels.push(harvestModel);
      // });

      // console.log('logging here', listModels);

      // this.gridOptions.api.setRowData(listModels);
      // let data = this.timelineHelper.createTimelineDataFromHarvestViewModelList(listModels)
      // this.timelineData = data.dataList
      // this.timelineColors = data.colors
      // this.gridOptions.api.hideOverlay();

      // this.gridOptions.columnApi.getColumn('startDate').setSort("desc")
      // this.setUpColumns();
      // //this.setTotalRow();
  }

  getStrainsFromHarvest(h: models.Harvest): string {
    // Use flatMap to both map and flatten the array in one operation
    let strains = h.phases?.flatMap(phase => 
      phase.strains?.filter(strain => strain.strainName != null).map(strain => strain.strainName)
    ) || []; // Fallback to empty array if h.phases is undefined
    
    let uniqueStrains = new Set(strains.filter(i => i != null)); // Create a Set to ensure uniqueness
    
    return [...uniqueStrains].join(', '); // Convert the Set back to an array and join into a string
  }

  getPlantCountFromHarvest(h: models.Harvest): number {
    let noneNullStrains = h.phases?.filter(i => i.strains != null) || [];
    if(noneNullStrains.length == 0) {
      return null;
    }

    for (let i = noneNullStrains.length-1; i >= 0; i--) {
      const phaseStrains = noneNullStrains[i];
      let counts = phaseStrains.strains.map(i => i.count);
      if(counts.length > 0) {
        var sum = counts.reduce((p, c) => {return p+c});
        if(sum > 0) {
          return sum;
        }
      }
    }
    return 0;
  }

  getZoneForHarvest(h: models.Harvest) {
    let noneNullStrains = (h.phases?.filter(i => i.strains != null) || []).filter(i => i.strains.length > 0);
    if(noneNullStrains.length == 0) {
      return null;
    }
    let lastPhaseStrains = noneNullStrains[noneNullStrains.length-1];
    let zoneIds = new Set(lastPhaseStrains.strains.filter(i => i.zones != null).flatMap(i => i.zones).map(i => i.zoneId).filter(i => i != null))
    let zones = this.zoneService.getAllTopLevel(this.zones, [...zoneIds]);
    return zones.map(i => i.name).join(', ')
  }

  // setTotalRow() {
  //   let numberCols = [];
  //   this.allPossibleColumns.forEach(col => {
  //     if(col.field.endsWith('_clone')){
  //       numberCols.push(col.field);
  //     }
  //   });
  //   // this.postHarvestDataPoints.filter(i => i.dataType == models.DataTypes.number).forEach(numberDataPoints => {
  //   //   numberCols.push(`${numberDataPoints.systemValue}_post`);
  //   // });
  //   this.gridPinningService.setPinnedData(this.gridOptions.api, this.allPossibleColumns.map(i => i.field), ['name'], [], numberCols);
  // }
  
  buildPhaseDataArray(events: models.Event[]) {
    const phases = [...new Set(events?.map(event => event.phase))]
    if (phases.length > this.maxPhases) {
      this.maxPhases = phases.length;
    }

    const phaseDataArray: viewmodels.HarvestTableViewPhaseModel[] = [];

    phases.forEach(phase => {
      const filteredEvents = events.filter(event => event.phase == phase)
      let startAndEndDate = this.timelineHelper.getStartAndEndDateForPhase(filteredEvents)
      let phaseData: viewmodels.HarvestTableViewPhaseModel = {
        name: phase,
        startDate: Timestamp.fromDate(startAndEndDate[0]),
        endDate: Timestamp.fromDate(startAndEndDate[1]),
      }

      phaseDataArray.push(phaseData);
    })

    return phaseDataArray
  }

  get canSeeHarvestAdd(): boolean {
    return this.claimsService.userRole?.permissions?.includes(models.Permissions.addHarvest);
  }

  addedHarvests(dialogResult: any) {
    // if(dialogResult.harvestIds && dialogResult.harvestIds.length > 0) {
    //   this.getHarvests();
    // }

    // if(dialogResult.linked && this.leaflogixSyncBtnComponent != null){
    //   this.leaflogixSyncBtnComponent.sync();
    // }
  }

  addHarvest() {
    if (!this.canAdd) {
      let restrictionDialog = this.dialog.open(RestrictionNotificationComponent, {
        width: '50%',
        data: 'harvestAdd'
      });
      return;
    }

    let dialogRef = this.dialog.open(HarvestAddComponent, {
      data: { addGroup: false }
    });

    dialogRef.afterClosed().subscribe(async result => {
      this.reloadHarvests();
    })
  }

  get canSeeHarvestGroupDelete(): boolean {
    return this.claimsService.userRole?.permissions?.includes(models.Permissions.deleteHarvest) ||
           this.claimsService.userRole?.permissions?.includes(models.Permissions.deleteGroup);
  }

  harvestGroupDelete() {
    let dialogRef = this.dialog.open(HarvestGroupDeleteComponent, {
      panelClass: 'full-width-dialog',
      data: { hideGroups: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.reloadHarvests();
    })
  }

  exportAsCSV() {
    // Build the CSV from the visible p-table columns (incl. dynamic phases).
    const cols: Array<{ header: string, value: (r: any) => string }> = [];
    cols.push({ header: 'Name', value: r => r.name ?? '' });
    if (this.showDutchieColumn) {
      cols.push({ header: 'Dutchie Name', value: r => r.leaflogixName ?? '' });
    }
    cols.push({ header: 'Clone Date', value: r => this.formatDate(r.startDate) });
    this.phaseColumns.forEach(p => cols.push({ header: p, value: r => this.formatDate(r[p]) }));
    cols.push({ header: 'Harvest Date', value: r => this.formatDate(r.harvestDate) });
    cols.push({ header: 'End Date', value: r => this.formatDate(r.endDate) });
    cols.push({ header: 'Completed', value: r => (r.percentComplete != null ? Math.round(r.percentComplete) : 0) + '%' });
    cols.push({ header: 'Strains', value: r => r.strains ?? '' });
    cols.push({ header: 'Plant Count', value: r => (r.plantCount != null ? String(r.plantCount) : '') });
    cols.push({ header: 'Zone', value: r => r.zone ?? '' });

    const esc = (s: any) => '"' + String(s ?? '').replace(/"/g, '""') + '"';
    const lines = [cols.map(c => esc(c.header)).join(',')];
    this.tableRows.forEach(r => lines.push(cols.map(c => esc(c.value(r))).join(',')));

    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'harvests.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async reset() {
    this.zoneIds = [];
    this.zoneSelectMultiBtnComponent.selected = [];
    this.harvestSelectMultiBtnComponent.selected = [];
    await this.harvestSelectMultiBtnComponent.getHarvests();
    this.templateSelectMultiBtnComponent.selected = [];
    await this.templateSelectMultiBtnComponent.getAll();
    this.filteredHarvestPhasesByZone = [];
    this.getHarvests();
  }

  async reloadHarvests() {
    //await this.helperService.wait(1500);
    this.harvestSelectMultiBtnComponent.selected = [];
    this.harvestSelectMultiBtnComponent.getHarvests();
  }
}
